/**
 * A chain, named the way the domain names it.
 *
 * The wire lists a chain once per channel, under a suffixed key: `kronan-retail` in store,
 * `kronan-online` on the web. The domain names the chain itself and carries the channel
 * beside it, so a slug never carries a suffix. The distinction is not cosmetic: the cache
 * key is built by appending the in-store suffix, and a wire key handed in as a slug hashes
 * to `kronan-retail-retail` -- a row that exists nowhere, read as an empty answer rather
 * than as the mistake it is.
 *
 * The brand makes that a compile error. `VendorSlug()` is the one way in, and it rejects
 * the suffixed forms.
 */

declare const __vendorSlugBrand: unique symbol;
export type VendorSlug = string & { readonly [__vendorSlugBrand]: never };

/** The channel suffixes the wire appends to a chain name. Never part of a slug. */
const CHANNEL_SUFFIXES: readonly string[] = ['-retail', '-online'];

/** The channel suffix a chain name still carries, or undefined. The one place that rule lives. */
function channelSuffixOf(normalized: string): string | undefined {
  return CHANNEL_SUFFIXES.find((candidate) => normalized.endsWith(candidate));
}

/**
 * The domain name of a chain read off the feed, or null when the feed's key does not name one.
 *
 * The boundary form of the constructor below, for the one caller that is parsing rather than
 * building: the ACL resolves a wire key to a chain and channel, and a key the resolver cannot
 * strip a channel off (`x-online`, or an empty one) leaves a value no query can be built from.
 * That is a violation of the feed's contract, which the ACL reports and skips -- so it needs
 * to ask the question without the answer being a thrown error.
 */
export function tryVendorSlug(raw: string): VendorSlug | null {
  const normalized = raw.trim();
  if (!normalized || channelSuffixOf(normalized)) return null;
  return normalized as VendorSlug;
}

/**
 * The domain name of a chain.
 *
 * Throws rather than returning null: this is not a boundary parser over feed data (the ACL
 * resolves wire keys with `resolveVendor` and rejects the ones that name no chain), it is the
 * constructor callers build a query with, and a suffixed value there is a programming mistake
 * that must not reach a read.
 */
export function VendorSlug(raw: string): VendorSlug {
  const normalized = raw.trim();
  if (!normalized) {
    throw new Error('Invalid VendorSlug: empty');
  }
  const suffix = channelSuffixOf(normalized);
  if (suffix) {
    throw new Error(
      `Invalid VendorSlug: "${raw}" carries the wire channel suffix "${suffix}"; ` +
        'pass the chain name and let the read decide the channel'
    );
  }
  return normalized as VendorSlug;
}
